import { Suspense, useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';

import CssBaseline from '@mui/material/CssBaseline';
import type { PaletteMode } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';

import { authApi } from 'Api';
import { LoginState, setLoggedIn, useSelectLoginState } from 'Auth/data';
import { Themes } from 'Consts/themes';
import { Router } from 'Router';
import { SignalRConnectionContextProvider } from 'SignalR';
import { useAppDispatch } from 'Store';
import { Colors } from 'Themes/colors';
import { getThemeDark } from 'Themes/themeDark';
import { getThemeLight } from 'Themes/themeLight';
import {
  getCurrentUserInfo,
  syncPreferencesFromUserData,
  useSelectCurrentUserInfo,
  useSelectUserTheme
} from 'User/data';

export const App = () => {
  const dispatch = useAppDispatch();
  const loginState = useSelectLoginState();
  const currentUserInfo = useSelectCurrentUserInfo();
  const applicationTheme = useSelectUserTheme();

  const hasInitializedUserData = useRef(false);
  const hasCheckedAuthStatus = useRef(false);

  const isObjectEmpty = (object: any) => Object.keys(object).length === 0;

  const getTheme = (applicationTheme: PaletteMode) => {
    if (applicationTheme && applicationTheme === Themes.dark) {
      return getThemeDark();
    }

    return getThemeLight();
  };

  const currentTheme = getTheme(applicationTheme);

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (hasCheckedAuthStatus.current) {
        return;
      }

      hasCheckedAuthStatus.current = true;

      try {
        await new Promise(resolve => setTimeout(resolve, 100));

        const result = await authApi.getCookie();
        if (result.isSuccess && result.data?.isAuthenticated) {
          dispatch(setLoggedIn());
        }
      } catch (error) {
        console.error('Failed to check authentication status:', error);
      }
    };

    checkAuthStatus();
  }, [dispatch]);

  useEffect(() => {
    if (loginState === LoginState.LOGGED && !hasInitializedUserData.current) {
      hasInitializedUserData.current = true;
      dispatch(getCurrentUserInfo());
    }

    if (loginState !== LoginState.LOGGED) {
      hasInitializedUserData.current = false;
    }

    if (loginState === LoginState.NOT_LOGGED) {
      hasCheckedAuthStatus.current = false;
    }
  }, [loginState, dispatch]);

  useEffect(() => {
    if (loginState === LoginState.LOGGED && !isObjectEmpty(currentUserInfo)) {
      dispatch(
        syncPreferencesFromUserData({
          theme: currentUserInfo.theme,
          languageCode: currentUserInfo.languageCode,
          timezone: currentUserInfo.timezone,
          isStartBannerHidden: currentUserInfo.isStartBannerHidden
        })
      );
    }
  }, [dispatch, loginState, currentUserInfo]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Suspense fallback="Loading">
        <SignalRConnectionContextProvider>
          <Router />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: Colors.green,
                color: Colors.white
              }
            }}
          />
        </SignalRConnectionContextProvider>
      </Suspense>
    </ThemeProvider>
  );
};
